import type {ReactElement} from 'react';
import {useRef} from 'react';
import {createPortal} from 'react-dom';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {POPUP_SUBMENU_MARKER_ATTR} from '../../poster-popup-menu.types';
import styles from './popup-submenu.module.scss';

interface Props {
  id: string;
  text: string;
  icon: string;
  items: ControlledListItemType[];
  isActive: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export function PopupSubmenu({id, text, icon, items, isActive, onMouseEnter, onMouseLeave}: Props): ReactElement {
  const selectorRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);

  const getSubmenuPosition = (): {top: number; left: number} => {
    const rect = selectorRef.current?.getBoundingClientRect();
    if (!rect) {
      return {top: 0, left: 0};
    }
    const submenuWidth = submenuRef.current?.offsetWidth ?? 0;
    const submenuHeight = submenuRef.current?.offsetHeight ?? 0;
    const left = Math.max(0, Math.min(rect.right, window.innerWidth - submenuWidth));
    const top = Math.max(0, Math.min(rect.top, window.innerHeight - submenuHeight));
    return {top, left};
  };

  const pos = isActive ? getSubmenuPosition() : {top: 0, left: 0};

  return (
    <div ref={selectorRef} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <ControlledListItemType7 id={id} text={text} icon={icon} rightIcon="icon-caret-right" rightIconClassName={styles.submenuArrow} />
      {isActive
        ? createPortal(
            <div
              ref={submenuRef}
              className={styles.submenuPopup}
              style={{top: pos.top, left: pos.left}}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              {...{[POPUP_SUBMENU_MARKER_ATTR]: ''}}
            >
              <ControlledList className="spacing-m-0 spacing-p-0" items={items} />
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
